import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { Link, useSearchParams } from 'react-router-dom';
import Layout from '../components/Layout/Layout';
import ExploreVariationsClasses from '../components/ExploreVariationsClasses';
import PreCompetitionClasses from '../components/PreCompetitionClasses';

// Import images
import artisticBallerina from '../assets/artistic_ballerina_shot.jpg';
import blackWhiteSwan from '../assets/black_and_white_swan_lake_solo.jpg';
import teenagerPreStage from '../assets/teenager_pre_stage.jpg';
import teenagerBalletClass from '../assets/tennager_ballet_class.jpg';
import variationConsulting from '../assets/variation_consulting.jpg';
import stagePasDeDeux from '../assets/stage_pas_de_deux.jpg';
import waitingBefore from '../assets/waiting_before_ballet_competition.jpg';
import lilacVariation from '../assets/lilac_variation.jpg';

const LearnExplore = () => {
  const [showExploreModal, setShowExploreModal] = useState(false);
  const [showConsultingModal, setShowConsultingModal] = useState(false);
  const [showPreCompModal, setShowPreCompModal] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const [isMobile, setIsMobile] = useState(typeof window !== 'undefined' ? window.innerWidth <= 768 : false);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const [consultingForm, setConsultingForm] = useState({
    coachingType: '',
    name: '',
    email: '',
    phone: '',
    division: '',
    variation: '',
    message: ''
  });
  const [consultingSubmitting, setConsultingSubmitting] = useState(false);
  const [consultingStatus, setConsultingStatus] = useState('');

  useEffect(() => {
    if (searchParams.get('variations') === 'open') {
      setShowExploreModal(true);
    }
    if (searchParams.get('preclasses') === 'open') {
      setShowPreCompModal(true);
    }
  }, [searchParams]);

  // Lock background scroll while the inline Consulting modal is open
  useEffect(() => {
    if (showConsultingModal) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      return () => { document.body.style.overflow = prev; };
    }
  }, [showConsultingModal]);

  const handleOpenModal = () => {
    setShowExploreModal(true);
    setSearchParams({ variations: 'open' });
  };

  const handleCloseModal = () => {
    setShowExploreModal(false);
    setSearchParams({});
  };

  const handleOpenPreCompModal = () => {
    setShowPreCompModal(true);
    setSearchParams({ preclasses: 'open' });
  };

  const handleClosePreCompModal = () => {
    setShowPreCompModal(false);
    setSearchParams({});
  };

  const handleConsultingChange = (e) => {
    setConsultingForm({ ...consultingForm, [e.target.name]: e.target.value });
  };

  const handleConsultingSubmit = async (e) => {
    e.preventDefault();
    setConsultingSubmitting(true);
    try {
      const response = await fetch('https://formspree.io/f/manjkjaa', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...consultingForm, submissionType: 'Variation Consulting Inquiry' }),
      });
      if (response.ok) {
        setConsultingStatus('submitted');
        setConsultingForm({ coachingType: '', name: '', email: '', phone: '', division: '', variation: '', message: '' });
      } else {
        setConsultingStatus('error');
      }
    } catch {
      setConsultingStatus('error');
    }
    setConsultingSubmitting(false);
  };

  const inputStyle = {
    width: '100%',
    padding: isMobile ? '0.95rem 1rem' : '0.75rem 1rem',
    border: '1px solid #ddd',
    borderRadius: '8px',
    fontSize: '16px', // 16px prevents iOS zoom-on-focus
    boxSizing: 'border-box',
    fontFamily: 'inherit',
    WebkitAppearance: 'none',
    appearance: 'none'
  };

  const labelStyle = {
    display: 'block',
    marginBottom: '0.4rem',
    fontWeight: '600',
    fontSize: '0.9rem',
    color: 'var(--charcoal)',
    textAlign: 'left'
  };

  return (
    <Layout
      title="Learn & Explore | Prix du Canada"
      description="Discover your dance potential with interactive quizzes, masterclasses, and expert guides. Everything you need to grow as a ballet dancer."
      keywords="ballet quizzes, ballet workshops, ballet masterclass, dance education, ballet training"
    >
      {/* Page Header */}
      <section style={{padding: '3rem 0 2rem', background: 'var(--warm-white)'}}>
        <div className="container">
          <p style={{textAlign: 'center', fontSize: '1.2rem', color: 'var(--charcoal)', maxWidth: '800px', margin: '0 auto', lineHeight: '1.7'}}>
            Discover your dance potential with our year-round programs, interactive quizzes, and expert guides to elevate your technique and performance. Everything you need to grow as a dancer, all in one place.
          </p>
        </div>
      </section>

      {/* PDC Events Section */}
      <section className="section" style={{padding: '4rem 0'}}>
        <div className="container">
          <h2 style={{textAlign: 'center', marginBottom: '1rem', fontSize: '2.2rem'}}>PDC Events</h2>
          <p style={{textAlign: 'center', fontSize: '1.2rem', color: 'var(--soft-gray)', margin: '0 auto 3rem', maxWidth: '600px'}}>Programs and workshops for dancers year-round</p>

          <div style={{display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: '2.5rem', margin: '0 auto', alignItems: 'stretch'}}>
            <div
              className="card"
              onClick={handleOpenPreCompModal}
              style={{ padding: '1.5rem', overflow: 'hidden', cursor: 'pointer', transition: 'transform 0.2s ease, box-shadow 0.2s ease', display: 'flex', flexDirection: 'column', height: '100%' }}
              onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-5px)'; e.currentTarget.style.boxShadow = '0 10px 25px rgba(0,0,0,0.1)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 4px 6px rgba(0,0,0,0.1)'; }}
            >
              <img src={teenagerBalletClass} alt="Pre-Competition Practice Classes" style={{width: '100%', height: '180px', objectFit: 'cover', borderRadius: '10px', marginBottom: '1.5rem'}} />
              <h3 style={{color: 'var(--deep-gold)', marginBottom: '1.2rem', fontSize: '1.4rem', lineHeight: '1.3'}}>Pre-Competition Practice Classes</h3>
              <p style={{lineHeight: '1.6', margin: '0 0 1rem', fontSize: '1.1rem'}}>Barre, variation practice, and a performing opportunity — with personalized corrections in every class.</p>
              <div style={{marginTop: 'auto', display: 'flex', alignItems: 'center', color: 'var(--deep-gold)', fontSize: '0.9rem', fontWeight: '600'}}>
                Sign up →
              </div>
            </div>

            <div
              className="card"
              onClick={() => setShowConsultingModal(true)}
              style={{ padding: '1.5rem', overflow: 'hidden', cursor: 'pointer', transition: 'transform 0.2s ease, box-shadow 0.2s ease', display: 'flex', flexDirection: 'column', height: '100%' }}
              onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-5px)'; e.currentTarget.style.boxShadow = '0 10px 25px rgba(0,0,0,0.1)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 4px 6px rgba(0,0,0,0.1)'; }}
            >
              <img src={variationConsulting} alt="Variation Consulting" style={{width: '100%', height: '180px', objectFit: 'cover', borderRadius: '10px', marginBottom: '1.5rem'}} />
              <h3 style={{color: 'var(--primary-gold)', marginBottom: '1.2rem', fontSize: '1.4rem', lineHeight: '1.3'}}>Variation Consulting</h3>
              <p style={{lineHeight: '1.6', margin: '0 0 1rem', fontSize: '1.1rem'}}>One-on-one guidance to select and perfect your competition variation</p>
              <div style={{marginTop: 'auto', display: 'flex', alignItems: 'center', color: 'var(--primary-gold)', fontSize: '0.9rem', fontWeight: '600'}}>
                Inquire now →
              </div>
            </div>

            <div
              className="card"
              onClick={handleOpenModal}
              style={{ padding: '1.5rem', overflow: 'hidden', cursor: 'pointer', transition: 'transform 0.2s ease, box-shadow 0.2s ease', display: 'flex', flexDirection: 'column', height: '100%' }}
              onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-5px)'; e.currentTarget.style.boxShadow = '0 10px 25px rgba(0,0,0,0.1)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 4px 6px rgba(0,0,0,0.1)'; }}
            >
              <div style={{position: 'relative', marginBottom: '1.5rem'}}>
                <img src={lilacVariation} alt="Explore Variations Series" style={{width: '100%', height: '180px', objectFit: 'cover', borderRadius: '10px', display: 'block'}} />
                <span style={{position: 'absolute', bottom: '12px', left: '12px', background: 'rgba(44, 44, 44, 0.85)', color: 'white', fontSize: '0.75rem', padding: '0.4rem 0.9rem', borderRadius: '15px', fontWeight: '600', letterSpacing: '0.5px', textTransform: 'uppercase', backdropFilter: 'blur(4px)', WebkitBackdropFilter: 'blur(4px)'}}>2026 Season Complete</span>
              </div>
              <h3 style={{color: 'var(--primary-gold)', marginBottom: '1.2rem', fontSize: '1.4rem', lineHeight: '1.3'}}>PDC Explore Variations Series</h3>
              <p style={{lineHeight: '1.6', margin: '0 0 1rem 0', fontSize: '1.1rem'}}>Our 4-week variation series returns in November for the 2027 season. Click for details and to be notified when new classes open.</p>
              <div style={{marginTop: 'auto', display: 'flex', alignItems: 'center', color: 'var(--deep-gold)', fontSize: '0.9rem', fontWeight: '600'}}>
                See past classes & what's next →
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Ballet Quizzes Section */}
      <section className="section" style={{background: 'var(--light-gray)', padding: '4rem 0'}}>
        <div className="container">
          <h2 style={{textAlign: 'center', marginBottom: '1rem', fontSize: '2.2rem'}}>Ballet Quizzes</h2>
          <p style={{textAlign: 'center', fontSize: '1.2rem', color: 'var(--soft-gray)', margin: '0 auto 3rem', maxWidth: '600px'}}>Discover your strengths and potential</p>

          <div style={{display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: '2.5rem', maxWidth: '900px', margin: '0 auto'}}>
            <div className="card" style={{padding: '1.5rem', overflow: 'hidden', position: 'relative'}}>
              <div style={{position: 'absolute', top: '15px', right: '15px', background: 'linear-gradient(135deg, #ff4757, #ff3742)', color: 'white', padding: '6px 12px', borderRadius: '12px', fontSize: '0.75rem', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '1px', boxShadow: '0 2px 8px rgba(255, 71, 87, 0.3)', zIndex: '10'}}>
                NEW
              </div>
              <img src={artisticBallerina} alt="Ballet Variation Quiz" style={{width: '100%', height: '200px', objectFit: 'cover', borderRadius: '10px', marginBottom: '1.5rem'}} />
              <h3 style={{color: 'var(--primary-gold)', marginBottom: '1.2rem', fontSize: '1.4rem', lineHeight: '1.3'}}>Which Ballet Variation is Built for You?</h3>
              <p style={{marginBottom: '2rem', lineHeight: '1.6', fontSize: '1.1rem'}}>AI-powered quiz to find your perfect variation match for this competition</p>
              <Link to="/quiz" style={{display: 'inline-block', padding: '0.8rem 2rem', background: 'var(--primary-gold)', color: 'white', textDecoration: 'none', borderRadius: '6px', fontWeight: '600', fontSize: '1rem', transition: 'transform 0.2s ease'}}>Take Quiz</Link>
            </div>

            <div className="card" style={{padding: '1.5rem', overflow: 'hidden', position: 'relative', opacity: '0.8'}}>
              <div style={{position: 'absolute', top: '15px', right: '15px', background: 'linear-gradient(135deg, #5f27cd, #341f97)', color: 'white', padding: '6px 12px', borderRadius: '12px', fontSize: '0.75rem', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '1px', boxShadow: '0 2px 8px rgba(95, 39, 205, 0.3)', zIndex: '10'}}>
                COMING SOON
              </div>
              <img src={blackWhiteSwan} alt="Ballet Character Quiz" style={{width: '100%', height: '200px', objectFit: 'cover', borderRadius: '10px', marginBottom: '1.5rem'}} />
              <h3 style={{color: 'var(--deep-gold)', marginBottom: '1.2rem', fontSize: '1.4rem', lineHeight: '1.3'}}>Which Ballet Character Are You?</h3>
              <p style={{marginBottom: '2rem', lineHeight: '1.6', fontSize: '1.1rem'}}>From fierce Odile to graceful Aurora—what's your vibe?</p>
              <button disabled style={{display: 'inline-block', padding: '0.8rem 2rem', background: '#ddd', color: '#999', border: 'none', borderRadius: '6px', fontWeight: '600', fontSize: '1rem', cursor: 'not-allowed'}}>Coming Soon</button>
            </div>
          </div>
        </div>
      </section>

      {/* Guides & Tips Section */}
      <section className="section" style={{padding: '4rem 0'}}>
        <div className="container">
          <h2 style={{textAlign: 'center', marginBottom: '1rem', fontSize: '2.2rem'}}>Guides & Tips</h2>
          <p style={{textAlign: 'center', fontSize: '1.2rem', color: 'var(--soft-gray)', margin: '0 auto 3rem', maxWidth: '600px'}}>Resources for dancers at every stage</p>

          <div style={{display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '2.5rem'}}>
            <Link to="/learn-explore/how-to-choose-variation" style={{textDecoration: 'none', color: 'inherit', display: 'flex', flexDirection: 'column'}}>
              <div className="card" style={{padding: '1.5rem', overflow: 'hidden', cursor: 'pointer', transition: 'transform 0.2s ease, box-shadow 0.2s ease', display: 'flex', flexDirection: 'column', height: '100%'}}
                onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-4px)'; e.currentTarget.style.boxShadow = '0 8px 20px rgba(0,0,0,0.1)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = ''; }}>
                <img src={stagePasDeDeux} alt="Variation Selection" style={{width: '100%', height: '160px', objectFit: 'cover', borderRadius: '10px', marginBottom: '1.5rem', flexShrink: 0}} />
                <h3 style={{color: 'var(--primary-gold)', marginBottom: '1.2rem', fontSize: '1.3rem', lineHeight: '1.3'}}>How to Choose the Right Variation</h3>
                <p style={{lineHeight: '1.6', margin: '0 0 1rem', fontSize: '1.1rem', flex: 1}}>Coach tips on matching your technique and personality to the perfect solo</p>
                <span style={{color: 'var(--primary-gold)', fontWeight: '600', fontSize: '0.95rem'}}>Read Guide →</span>
              </div>
            </Link>

            <Link to="/learn-explore/audition-prep" style={{textDecoration: 'none', color: 'inherit', display: 'flex', flexDirection: 'column'}}>
              <div className="card" style={{padding: '1.5rem', overflow: 'hidden', cursor: 'pointer', transition: 'transform 0.2s ease, box-shadow 0.2s ease', display: 'flex', flexDirection: 'column', height: '100%'}}
                onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-4px)'; e.currentTarget.style.boxShadow = '0 8px 20px rgba(0,0,0,0.1)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = ''; }}>
                <img src={waitingBefore} alt="Audition Prep" style={{width: '100%', height: '160px', objectFit: 'cover', borderRadius: '10px', marginBottom: '1.5rem', flexShrink: 0}} />
                <h3 style={{color: 'var(--deep-gold)', marginBottom: '1.2rem', fontSize: '1.3rem', lineHeight: '1.3'}}>Audition Prep 101</h3>
                <p style={{lineHeight: '1.6', margin: '0 0 1rem', fontSize: '1.1rem', flex: 1}}>Make the most of your time on stage—physically and mentally</p>
                <span style={{color: 'var(--deep-gold)', fontWeight: '600', fontSize: '0.95rem'}}>Read Guide →</span>
              </div>
            </Link>

            <Link to="/learn-explore/beginners-guide" style={{textDecoration: 'none', color: 'inherit', display: 'flex', flexDirection: 'column'}}>
              <div className="card" style={{padding: '1.5rem', overflow: 'hidden', cursor: 'pointer', transition: 'transform 0.2s ease, box-shadow 0.2s ease', display: 'flex', flexDirection: 'column', height: '100%'}}
                onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-4px)'; e.currentTarget.style.boxShadow = '0 8px 20px rgba(0,0,0,0.1)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = ''; }}>
                <img src={teenagerPreStage} alt="Beginner's Guide" style={{width: '100%', height: '160px', objectFit: 'cover', borderRadius: '10px', marginBottom: '1.5rem', flexShrink: 0}} />
                <h3 style={{color: 'var(--primary-gold)', marginBottom: '1.2rem', fontSize: '1.3rem', lineHeight: '1.3'}}>Beginner's Guide to Ballet Competitions</h3>
                <p style={{lineHeight: '1.6', margin: '0 0 1rem', fontSize: '1.1rem', flex: 1}}>Perfect for first-time competitors and their families</p>
                <span style={{color: 'var(--primary-gold)', fontWeight: '600', fontSize: '0.95rem'}}>Read Guide →</span>
              </div>
            </Link>
          </div>
        </div>
      </section>

      {/* Explore Variations Modal */}
      <ExploreVariationsClasses
        isOpen={showExploreModal}
        onClose={handleCloseModal}
      />

      {/* Pre-Competition Classes Modal */}
      <PreCompetitionClasses
        isOpen={showPreCompModal}
        onClose={handleClosePreCompModal}
      />

      {/* Variation Consulting Modal (portalled to body to escape any parent stacking context) */}
      {showConsultingModal && ReactDOM.createPortal(
        <div
          style={{
            position: 'fixed', inset: 0, backgroundColor: 'rgba(0, 0, 0, 0.8)', zIndex: 9999,
            display: 'flex', alignItems: isMobile ? 'flex-end' : 'center', justifyContent: 'center',
            padding: isMobile ? '0' : '20px',
            WebkitOverflowScrolling: 'touch'
          }}
          onClick={(e) => { if (e.target === e.currentTarget) { setShowConsultingModal(false); setConsultingStatus(''); } }}
        >
          <div style={{
            backgroundColor: 'white',
            borderRadius: isMobile ? '16px 16px 0 0' : '15px',
            maxWidth: '580px', width: '100%',
            maxHeight: isMobile ? '95dvh' : '90vh',
            height: isMobile ? '95dvh' : 'auto',
            overflow: 'hidden',
            position: 'relative',
            display: 'flex',
            flexDirection: 'column',
            paddingTop: isMobile ? 'env(safe-area-inset-top)' : 0,
            paddingBottom: isMobile ? 'env(safe-area-inset-bottom)' : 0
          }}>

            {/* Mobile Handle Bar */}
            {isMobile && (
              <div style={{ display: 'flex', justifyContent: 'center', padding: '8px 0 4px', flexShrink: 0 }}>
                <div style={{ width: '40px', height: '4px', background: '#ddd', borderRadius: '2px' }} />
              </div>
            )}

            {/* Sticky Close */}
            <button
              onClick={() => { setShowConsultingModal(false); setConsultingStatus(''); }}
              style={{
                position: 'absolute',
                top: isMobile ? 'calc(env(safe-area-inset-top) + 10px)' : '14px',
                right: isMobile ? '10px' : '14px',
                background: 'rgba(255, 255, 255, 0.95)',
                backdropFilter: 'blur(6px)',
                WebkitBackdropFilter: 'blur(6px)',
                border: '1px solid rgba(0,0,0,0.08)',
                boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                fontSize: '22px', cursor: 'pointer',
                color: 'var(--charcoal)', zIndex: 20,
                width: '44px', height: '44px',
                borderRadius: '50%',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                lineHeight: 1, padding: 0,
                WebkitTapHighlightColor: 'transparent',
                transition: 'transform 0.15s ease'
              }}
              onTouchStart={(e) => { e.currentTarget.style.transform = 'scale(0.92)'; }}
              onTouchEnd={(e) => { e.currentTarget.style.transform = 'scale(1)'; }}
              aria-label="Close"
            >
              ×
            </button>

            {/* Scrollable Inner */}
            <div style={{
              overflowY: 'auto', overflowX: 'hidden', flex: 1,
              WebkitOverflowScrolling: 'touch',
              overscrollBehavior: 'contain'
            }}>

            {/* Header */}
            <div style={{ padding: isMobile ? '1.5rem 1.25rem 1.25rem' : '2.5rem 2.5rem 1.5rem', borderBottom: '1px solid #eee' }}>
              <h2 style={{ color: 'var(--primary-gold)', marginBottom: '0.5rem', fontSize: '1.8rem' }}>Variation Consulting</h2>
              <p style={{ color: 'var(--soft-gray)', margin: 0, lineHeight: '1.6' }}>
                Work with an experienced coach to find and perfect the ideal variation for your level, style, and competition goals.
              </p>
            </div>

            {/* Coaching type cards */}
            <div style={{ padding: '1.5rem 2.5rem 0' }}>
              <p style={{ ...labelStyle, marginBottom: '0.75rem', fontSize: '0.95rem' }}>Select a coaching format *</p>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem', marginBottom: '1.5rem' }}>
                {[
                  { value: 'private', label: 'Private (1-on-1)', desc: 'Dedicated one-on-one sessions tailored entirely to you' },
                  { value: 'semi-private', label: 'Semi-Private', desc: 'Small group of 2–3 dancers sharing coaching time' },
                ].map((opt) => (
                  <label
                    key={opt.value}
                    style={{
                      display: 'block',
                      padding: '1rem',
                      border: `2px solid ${consultingForm.coachingType === opt.value ? 'var(--primary-gold)' : '#ddd'}`,
                      borderRadius: '8px',
                      cursor: 'pointer',
                      background: consultingForm.coachingType === opt.value ? 'var(--soft-cream)' : 'white',
                      transition: 'all 0.2s ease'
                    }}
                  >
                    <input
                      type="radio"
                      name="coachingType"
                      value={opt.value}
                      checked={consultingForm.coachingType === opt.value}
                      onChange={handleConsultingChange}
                      style={{ display: 'none' }}
                    />
                    <p style={{ margin: '0 0 0.3rem', fontWeight: '700', fontSize: '0.95rem', color: consultingForm.coachingType === opt.value ? 'var(--primary-gold)' : 'var(--charcoal)' }}>{opt.label}</p>
                    <p style={{ margin: 0, fontSize: '0.82rem', color: 'var(--soft-gray)', lineHeight: '1.4' }}>{opt.desc}</p>
                  </label>
                ))}
              </div>
            </div>

            {/* Form */}
            {consultingStatus === 'submitted' ? (
              <div style={{ padding: '2rem 2.5rem 2.5rem', textAlign: 'center' }}>
                <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>✓</div>
                <h3 style={{ color: 'var(--primary-gold)', marginBottom: '0.75rem' }}>Inquiry Sent!</h3>
                <p style={{ color: 'var(--charcoal)', lineHeight: '1.6', marginBottom: '1.5rem' }}>
                  Thank you for your interest in Variation Consulting. We'll be in touch shortly with availability and pricing details.
                </p>
                <button
                  onClick={() => { setShowConsultingModal(false); setConsultingStatus(''); }}
                  style={{ padding: '0.75rem 2rem', background: 'var(--primary-gold)', color: 'white', border: 'none', borderRadius: '6px', fontWeight: '600', fontSize: '1rem', cursor: 'pointer' }}
                >
                  Close
                </button>
              </div>
            ) : (
              <form onSubmit={handleConsultingSubmit} style={{ padding: '0 2.5rem 2.5rem', display: 'grid', gap: '1rem' }}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                  <div>
                    <label style={labelStyle}>Name *</label>
                    <input type="text" name="name" value={consultingForm.name} onChange={handleConsultingChange} required style={inputStyle} placeholder="Your full name" />
                  </div>
                  <div>
                    <label style={labelStyle}>Email *</label>
                    <input type="email" name="email" value={consultingForm.email} onChange={handleConsultingChange} required style={inputStyle} placeholder="your@email.com" />
                  </div>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                  <div>
                    <label style={labelStyle}>Phone <span style={{ fontWeight: 400, color: 'var(--soft-gray)' }}>(optional)</span></label>
                    <input type="tel" name="phone" value={consultingForm.phone} onChange={handleConsultingChange} style={inputStyle} placeholder="+1 (416) 000-0000" />
                  </div>
                  <div>
                    <label style={labelStyle}>Division / Age Group *</label>
                    <select name="division" value={consultingForm.division} onChange={handleConsultingChange} required style={{ ...inputStyle, backgroundColor: 'white' }}>
                      <option value="">Select division</option>
                      <option value="Ages 7–9">Ages 7–9</option>
                      <option value="Ages 10–11">Ages 10–11</option>
                      <option value="Ages 12–14">Ages 12–14</option>
                      <option value="Ages 15–17">Ages 15–17</option>
                      <option value="Ages 18+ Competitive">Ages 18+ Competitive</option>
                      <option value="Adult Recreational">Adult Recreational</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label style={labelStyle}>Variation in mind <span style={{ fontWeight: 400, color: 'var(--soft-gray)' }}>(optional)</span></label>
                  <input type="text" name="variation" value={consultingForm.variation} onChange={handleConsultingChange} style={inputStyle} placeholder="e.g. Kitri Act III, Esmeralda, or unsure" />
                </div>

                <div>
                  <label style={labelStyle}>Additional details <span style={{ fontWeight: 400, color: 'var(--soft-gray)' }}>(optional)</span></label>
                  <textarea name="message" value={consultingForm.message} onChange={handleConsultingChange} rows={3} style={{ ...inputStyle, resize: 'vertical' }} placeholder="Tell us about your goals, experience, or any questions you have..." />
                </div>

                {consultingStatus === 'error' && (
                  <p style={{ color: '#dc3545', fontSize: '0.9rem', margin: 0 }}>Something went wrong. Please try again.</p>
                )}

                <button
                  type="submit"
                  disabled={consultingSubmitting || !consultingForm.coachingType}
                  style={{
                    padding: isMobile ? '1rem 1.5rem' : '0.9rem 1.5rem',
                    minHeight: isMobile ? '54px' : 'auto',
                    background: consultingSubmitting || !consultingForm.coachingType ? '#ccc' : 'var(--primary-gold)',
                    color: 'white',
                    border: 'none',
                    borderRadius: '10px',
                    fontSize: isMobile ? '1.05rem' : '1rem',
                    fontWeight: '700',
                    cursor: consultingSubmitting || !consultingForm.coachingType ? 'not-allowed' : 'pointer',
                    transition: 'background 0.2s ease, transform 0.1s ease',
                    letterSpacing: '0.5px',
                    fontFamily: 'inherit',
                    WebkitTapHighlightColor: 'transparent',
                    boxShadow: consultingSubmitting || !consultingForm.coachingType ? 'none' : '0 4px 12px rgba(201, 169, 110, 0.25)'
                  }}
                  onTouchStart={(e) => { if (!consultingSubmitting && consultingForm.coachingType) e.currentTarget.style.transform = 'scale(0.98)'; }}
                  onTouchEnd={(e) => { e.currentTarget.style.transform = 'scale(1)'; }}
                >
                  {consultingSubmitting ? 'Sending...' : 'Send Inquiry'}
                </button>
              </form>
            )}
            </div>
          </div>
        </div>,
        document.body
      )}
    </Layout>
  );
};

export default LearnExplore;
